/**
 * Schemas for the neighborhoods marketing-city surface (API-1379+).
 *
 * Today this hosts the `?city=...` list query schema. Detail/density/venues/
 * categories endpoints will extend this module additively.
 */

import { z } from 'zod';

// Sane pagination ceiling for the list query — NOT the city's neighborhood
// count (API-1620 / PIP-178). City totals grow (CLT seed is 23); UI ordinals
// ("NN of MM") read the live total from the detail/list payload. Keep this
// high enough for full-city fetches without unbounded SQL LIMITs.
export const NEIGHBORHOODS_MAX_LIMIT = 50;

// Strict boolean coercion — `z.coerce.boolean()` would treat any non-empty
// string (including "no" / "yes" / "0") as true. The marquee is editorial
// surface; ambiguity in the query string is a 400, not a silent acceptance.
const booleanQueryParamSchema = z.enum(['true', 'false']).transform((value) => value === 'true');

/**
 * City query fragment shared by neighborhood detail / categories (and the
 * list schema below). Intentionally does NOT carry list-only flags
 * (`featuredOnHome`, `limit`, `include`) — those endpoints previously reused
 * `neighborhoodsListQuerySchema` and Zod's default strip of unknown keys
 * kept stray query params harmless. Putting API-1657's strict `include`
 * refine on the shared schema would 400 detail/categories for
 * `?include=…` values develop accepted.
 */
export const neighborhoodsCityQuerySchema = z.object({
  city: z
    .string({ required_error: 'city is required' })
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'city must be a 3-letter uppercase code'),
});

export type NeighborhoodsCityQuery = z.infer<typeof neighborhoodsCityQuerySchema>;

// API-1657: opt-in projections. Today only `boundary` is recognised; unknown
// tokens 400 so clients cannot silently misspell the flag and ship without
// footprints. Accepts a single token or a comma-separated list (and Express's
// repeated-key array form) so `?include=boundary` and `?include=boundary,…`
// parse identically. LIST-ONLY — do not add to neighborhoodsCityQuerySchema.
const neighborhoodsIncludeQuerySchema = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform((value): string[] => {
    if (value === undefined) return [];
    const raw = Array.isArray(value) ? value.join(',') : value;
    return raw
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter((token) => token.length > 0);
  })
  .refine((tokens) => tokens.every((token) => token === 'boundary'), {
    message: 'include supports only: boundary',
  });

export const neighborhoodsListQuerySchema = neighborhoodsCityQuerySchema.extend({
  // API-1393: editorial marquee filter. Optional + boolean — when absent,
  // the existing list behavior is preserved (returns all neighborhoods for
  // the city).
  featuredOnHome: booleanQueryParamSchema.optional(),
  // API-1393: pairs with featuredOnHome=true for the unauthenticated city
  // homepage marquee (typically limit=4). Optional + clamped to
  // [1, NEIGHBORHOODS_MAX_LIMIT] so the SQL LIMIT can trust the value
  // without re-validating it.
  limit: z.coerce
    .number()
    .int()
    .transform((val) => Math.max(1, Math.min(val, NEIGHBORHOODS_MAX_LIMIT)))
    .optional(),
  // API-1657: opt-in simplified boundary + bbox projection. Absent → empty
  // array (response shape unchanged from develop).
  include: neighborhoodsIncludeQuerySchema,
});

export type NeighborhoodsListQuery = z.infer<typeof neighborhoodsListQuerySchema>;

// GeoJSON geometry schemas shared by the list (API-1584) and detail (API-1380)
// neighborhood surfaces. Loosely validated — the canonical source of truth is
// PostGIS `ST_AsGeoJSON` output, and over-tight runtime validation here would
// only mask geometry produced by a known-good query path.
export const geoJsonPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.array(z.number()))),
});

export type GeoJsonPolygon = z.infer<typeof geoJsonPolygonSchema>;

export const geoJsonPointSchema = z.object({
  type: z.literal('Point'),
  coordinates: z.array(z.number()),
});

export type GeoJsonPoint = z.infer<typeof geoJsonPointSchema>;

// RFC 7946 bounding box: [minLon, minLat, maxLon, maxLat]. Used by the
// API-1657 opt-in boundary projection so clients can normalise each
// footprint into a viewBox without measuring the ring.
export const geoJsonBboxSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]);

export type GeoJsonBbox = z.infer<typeof geoJsonBboxSchema>;

export const neighborhoodListItemSchema = z.object({
  slug: z.string(),
  name: z.string(),
  position: z.number().int().positive(),
  blurbTeaser: z.string(),
  walkScore: z.number().int().nullable(),
  peakHourLabel: z.string().nullable(),
  eventsThisWeek: z.number().int().nonnegative(),
  // API-1584: optional polygon + centroid geometry (mirrors the detail
  // endpoint). Emitted only when the PostGIS `ST_AsGeoJSON` output passes the
  // runtime GeoJSON guards in the route; omitted otherwise so consumers that
  // don't render maps are unaffected. Reuses the canonical GeoJSON schemas
  // declared just above.
  polygon: geoJsonPolygonSchema.optional(),
  centroid: geoJsonPointSchema.optional(),
  // API-1657: opt-in simplified footprint + bbox. Present only when the
  // caller passes `?include=boundary` AND the simplified geometry passes the
  // route's GeoJSON / bbox guards; omitted otherwise (never a placeholder).
  boundary: geoJsonPolygonSchema.optional(),
  bbox: geoJsonBboxSchema.optional(),
});

export type NeighborhoodListItem = z.infer<typeof neighborhoodListItemSchema>;

export const neighborhoodsListResponseSchema = z.object({
  neighborhoods: z.array(neighborhoodListItemSchema),
  // API-1686: count of neighborhoods matching the request's city (+
  // `featuredOnHome`) predicate, independent of the page `LIMIT`. City-total
  // copy must bind to this, NOT `neighborhoods.length` — the latter silently
  // caps at `NEIGHBORHOODS_MAX_LIMIT` (50) once a city indexes more than that.
  //
  // OPTIONAL on read, deliberately: Redis entries written before API-1686
  // carry no `total` and must still parse (Zod would otherwise reject the
  // whole payload and degrade the surface). Consumers fall back to the page
  // length when it is absent — never a fabricated larger number.
  total: z.number().int().nonnegative().optional(),
});

export type NeighborhoodsListResponse = z.infer<typeof neighborhoodsListResponseSchema>;

// API-1383: 30-day category breakdown for a neighborhood. Always 6 entries
// in the order live-music, food-drink, late-night, comedy, art, free.
export const neighborhoodCategoryKeySchema = z.enum([
  'live-music',
  'food-drink',
  'late-night',
  'comedy',
  'art',
  'free',
]);

export type NeighborhoodCategoryKey = z.infer<typeof neighborhoodCategoryKeySchema>;

export const neighborhoodCategoryAccentSchema = z.enum(['skyline', 'ember', 'glow', 'neutral']);

export type NeighborhoodCategoryAccent = z.infer<typeof neighborhoodCategoryAccentSchema>;

export const neighborhoodCategoryEntrySchema = z.object({
  category: neighborhoodCategoryKeySchema,
  label: z.string(),
  count: z.number().int().nonnegative(),
  share: z.number().min(0).max(1),
  accent: neighborhoodCategoryAccentSchema,
});

export type NeighborhoodCategoryEntry = z.infer<typeof neighborhoodCategoryEntrySchema>;

export const neighborhoodCategoriesResponseSchema = z.object({
  categories: z.array(neighborhoodCategoryEntrySchema),
});

export type NeighborhoodCategoriesResponse = z.infer<typeof neighborhoodCategoriesResponseSchema>;

// API-1381: 12-week hourly density composite for a neighborhood. Returns a
// 24-element vector (events active per hour) for a specific weekday, or the
// weekly average when `day=all`.
export const neighborhoodDensityDayParamSchema = z
  .enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'all'])
  .default('fri');

export type NeighborhoodDensityDayParam = z.infer<typeof neighborhoodDensityDayParamSchema>;

export const neighborhoodDensityQuerySchema = z.object({
  city: z
    .string({ required_error: 'city is required' })
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'city must be a 3-letter uppercase code'),
  day: neighborhoodDensityDayParamSchema,
});

export type NeighborhoodDensityQuery = z.infer<typeof neighborhoodDensityQuerySchema>;

export const neighborhoodDensityFeelSchema = z.object({
  timeRange: z.string(),
  name: z.string(),
  desc: z.string(),
  intensity: z.number().min(0).max(1),
});

export type NeighborhoodDensityFeel = z.infer<typeof neighborhoodDensityFeelSchema>;

export const neighborhoodDensityResponseSchema = z.object({
  day: neighborhoodDensityDayParamSchema,
  hourly: z.array(z.number().int().nonnegative()).length(24),
  peakHours: z.array(z.number().int().min(0).max(23)),
  feels: z.array(neighborhoodDensityFeelSchema),
});

export type NeighborhoodDensityResponse = z.infer<typeof neighborhoodDensityResponseSchema>;

// API-1380: per-neighborhood detail endpoint.
//
// `polygon` and `centroid` are emitted as GeoJSON (loosely validated — the
// canonical source of truth is PostGIS `ST_AsGeoJSON` output, and over-tight
// runtime validation here would only mask geometry produced by a known-good
// query path).
export const neighborhoodBearingSchema = z.enum(['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']);

export type NeighborhoodBearing = z.infer<typeof neighborhoodBearingSchema>;

export const neighborhoodAdjacentSchema = z.object({
  slug: z.string(),
  name: z.string(),
  distanceMi: z.number().nonnegative(),
  bearing: neighborhoodBearingSchema,
});

export type NeighborhoodAdjacent = z.infer<typeof neighborhoodAdjacentSchema>;

export const neighborhoodTransitSchema = z.object({
  lynx: z.string(),
  fromUptown: z.string(),
  fromNoDa: z.string(),
  parking: z.string(),
  bikeLane: z.string(),
});

export type NeighborhoodTransit = z.infer<typeof neighborhoodTransitSchema>;

export const neighborhoodDetailSchema = z.object({
  slug: z.string(),
  name: z.string(),
  position: z.number().int().positive(),
  totalCount: z.number().int().positive(),
  blurb: z.string(),
  walkScore: z.number().int().nullable(),
  peakHourLabel: z.string().nullable(),
  areaSqMi: z.number().nonnegative(),
  // WEB-843: null when no researched transit exists — never invent a default.
  transit: neighborhoodTransitSchema.nullable(),
  polygon: geoJsonPolygonSchema,
  centroid: geoJsonPointSchema,
  adjacent: z.array(neighborhoodAdjacentSchema),
});

export type NeighborhoodDetail = z.infer<typeof neighborhoodDetailSchema>;

export const neighborhoodDetailResponseSchema = z.object({
  neighborhood: neighborhoodDetailSchema,
});

export type NeighborhoodDetailResponse = z.infer<typeof neighborhoodDetailResponseSchema>;

// API-1382: ranked top-venues breakdown for a neighborhood. Configurable
// limit (default 5, max 20). `slug` is the canonical venue slug if/when the
// Location model gains one; today the route emits a stable fallback slug
// derived from city + venue name (see top-venues.ts for the convention).
export const TOP_VENUES_DEFAULT_LIMIT = 5;
export const TOP_VENUES_MAX_LIMIT = 20;

export const topVenuesQuerySchema = z.object({
  city: z
    .string({ required_error: 'city is required' })
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'city must be a 3-letter uppercase code'),
  limit: z.coerce
    .number()
    .int()
    .min(1, 'limit must be >= 1')
    .max(TOP_VENUES_MAX_LIMIT, `limit must be <= ${TOP_VENUES_MAX_LIMIT}`)
    .default(TOP_VENUES_DEFAULT_LIMIT),
});

export type TopVenuesQuery = z.infer<typeof topVenuesQuerySchema>;

export const topVenueEntrySchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  capacity: z.number().int().nonnegative().nullable(),
  // 'music' | 'jazz' | 'food' | 'bar' | 'gallery' | ... — sourced from the
  // Location.venueType enum and lowercased / aliased in the route layer.
  type: z.string().min(1),
  address: z.string(),
  upcomingCount: z.number().int().nonnegative(),
  signalScore: z.number(),
});

export type TopVenueEntry = z.infer<typeof topVenueEntrySchema>;

export const topVenuesResponseSchema = z.object({
  venues: z.array(topVenueEntrySchema),
});

export type TopVenuesResponse = z.infer<typeof topVenuesResponseSchema>;
